import React from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { UserPlus, Info } from 'lucide-react';

const RECRUITMENT_METHODS = [
  { id: 'email', label: 'Email invitation' },
  { id: 'poster', label: 'Posters / Flyers' },
  { id: 'social_media', label: 'Social media' },
  { id: 'website', label: 'Website / Online platform' },
  { id: 'word_of_mouth', label: 'Word of mouth' },
  { id: 'gatekeeper', label: 'Via gatekeeper / organization' },
  { id: 'existing_database', label: 'Existing participant database' },
  { id: 'snowball', label: 'Snowball sampling' },
  { id: 'public_records', label: 'Public records' },
  { id: 'clinical', label: 'Clinical referral' },
  { id: 'classroom', label: 'Classroom announcement' },
  { id: 'other', label: 'Other' }
];

function D5_Recruitment() {
  const { dataE, updateDataRaw, nextStep, previousStep } = useWizard();
  const recruitment = dataE.recruitment;

  const updateRecruitment = (field, value) => {
    updateDataRaw({
      'wizardE.recruitment': { ...recruitment, [field]: value }
    });
  };

  const toggleMethod = (methodId) => {
    const currentMethods = recruitment.methods || [];
    const newMethods = currentMethods.includes(methodId)
      ? currentMethods.filter(m => m !== methodId)
      : [...currentMethods, methodId];
    updateRecruitment('methods', newMethods);
  };

  const isComplete = recruitment.methods.length > 0 &&
                     recruitment.consentProcess &&
                     recruitment.withdrawalProcedure;

  return (
    <div className="wizard-step">
      <div className="step-header">
        <UserPlus className="step-icon" size={32} />
        <div>
          <h2>Step 5: Recruitment & Consent</h2>
          <p className="step-description">
            Describe how participants will be recruited and how you will obtain their informed consent.
          </p>
        </div>
      </div>

      <div className="form-section">
        <h3>Recruitment Methods</h3>

        <div className="form-group">
          <label>How will you recruit participants? *</label>
          <div className="checkbox-grid">
            {RECRUITMENT_METHODS.map(method => (
              <label key={method.id} className="checkbox-option">
                <input
                  type="checkbox"
                  checked={recruitment.methods?.includes(method.id) || false}
                  onChange={() => toggleMethod(method.id)}
                />
                <span>{method.label}</span>
              </label>
            ))}
          </div>
        </div>

        {recruitment.methods?.includes('other') && (
          <div className="form-group">
            <label>Please specify other recruitment method:</label>
            <input
              type="text"
              value={recruitment.otherMethod}
              onChange={(e) => updateRecruitment('otherMethod', e.target.value)}
              placeholder="Describe the other recruitment method"
            />
          </div>
        )}

        <div className="form-group">
          <label>Initial Contact Information</label>
          <textarea
            value={recruitment.initialInfo}
            onChange={(e) => updateRecruitment('initialInfo', e.target.value)}
            placeholder="What information will potential participants receive when first contacted? Include details about how you will ensure they have sufficient information to decide whether to participate."
            rows={4}
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Informed Consent</h3>

        <div className="info-box info">
          <Info size={20} />
          <div>
            <strong>Informed Consent Requirements</strong>
            <p>
              Participants must give voluntary, informed consent before taking part. They should understand 
              what participation involves, any risks or benefits, how their data will be used, and their 
              right to withdraw.
            </p>
          </div>
        </div>

        <div className="form-group">
          <label>Consent Process *</label>
          <textarea
            value={recruitment.consentProcess}
            onChange={(e) => updateRecruitment('consentProcess', e.target.value)}
            placeholder="Describe how you will obtain informed consent (e.g., written consent form, verbal consent, online consent). Include details about how you will ensure participants understand what they are consenting to."
            rows={5}
          />
        </div>

        <div className="form-group">
          <label>Withdrawal Procedure *</label>
          <textarea
            value={recruitment.withdrawalProcedure}
            onChange={(e) => updateRecruitment('withdrawalProcedure', e.target.value)}
            placeholder="Describe how participants can withdraw from the study, what happens to their data if they withdraw, and any time limits for withdrawal (e.g., until data is anonymized)."
            rows={4}
          />
        </div>

        <div className="form-group">
          <label>Debrief Procedure</label>
          <textarea
            value={recruitment.debriefProcedure}
            onChange={(e) => updateRecruitment('debriefProcedure', e.target.value)}
            placeholder="Describe how participants will be debriefed after the study (if applicable). This is particularly important if any deception was used."
            rows={3}
          />
        </div>
      </div>

      <NavigationButtons
        onPrevious={previousStep}
        onNext={isComplete ? nextStep : null}
        disableNext={!isComplete}
        nextLabel="Continue to Data Collection"
      />
    </div>
  );
}

export default D5_Recruitment;
